import type { CSSProperties } from 'react';
import { forwardRef } from 'react';

import { TOKEN_ELEMENT_MAP } from 'styles/config/text';

import { cn } from 'utils/cn';
import { colorCssVar } from 'utils/colorCssVar';
import type { Props } from './types';

const VARIANT_CLASSES: Record<string, string> = {
  headerExtraLarge:
    'font-semibold tracking-tight text-header-extra-large leading-tight',
  headerLarge: 'font-semibold tracking-tight text-header-large leading-tight',
  headerMedium: 'font-semibold tracking-tight text-header-medium leading-tight',
  headerRegular:
    'font-semibold tracking-tight text-header-regular leading-tight',
  headerSmall:
    'font-semibold text-body-small uppercase [letter-spacing:5%] leading-tight',
  body: 'font-regular text-body-regular leading-tight',
  bodySemibold: 'font-semibold text-body-regular leading-tight',
  bodySmall: 'font-regular text-body-small leading-tight',
  bodySmallSemibold: 'font-semibold text-body-small leading-tight',
  monospace: 'font-monospace font-regular text-monospace leading-normal',
};

const ALIGN: Record<string, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
  justify: 'text-justify',
  start: 'text-start',
  end: 'text-end',
};

const WHITESPACE: Record<string, string> = {
  normal: 'whitespace-normal',
  nowrap: 'whitespace-nowrap',
  pre: 'whitespace-pre',
  'pre-line': 'whitespace-pre-line',
  'pre-wrap': 'whitespace-pre-wrap',
  'break-spaces': 'whitespace-break-spaces',
};

const TEXT_TRANSFORM: Record<string, string> = {
  uppercase: 'uppercase',
  lowercase: 'lowercase',
  capitalize: 'capitalize',
  none: 'normal-case',
};

const DECORATION: Record<string, string> = {
  underline: 'underline',
  overline: 'overline',
  'line-through': 'line-through',
  none: 'no-underline',
};

const SIZE_TO_TW: Record<string, string> = {
  'body.regular': 'text-body-regular',
  'body.small': 'text-body-small',
  'header.extraLarge': 'text-header-extra-large',
  'header.large': 'text-header-large',
  'header.medium': 'text-header-medium',
  'header.regular': 'text-header-regular',
  'header.small': 'text-header-small',
};

const Text = forwardRef<
  HTMLElement,
  Omit<React.HTMLProps<HTMLElement>, keyof Props> & Props
>(
  (
    {
      as: customAs,
      children,
      variant = 'body',
      align,
      color,
      decoration,
      decorationColor,
      font,
      fontStyle,
      letterSpacing,
      lineHeight,
      size,
      textTransform,
      weight,
      whiteSpace,
      className,
      style,
      ...rest
    },
    ref,
  ) => {
    const as = (customAs ||
      TOKEN_ELEMENT_MAP[variant]) as keyof React.JSX.IntrinsicElements;

    // Only use inline style for properties without clean TW equivalents
    const inlineStyle: CSSProperties = {};
    if (decoration && !DECORATION[decoration]) {
      inlineStyle.textDecoration = decoration;
    }
    if (decorationColor) {
      inlineStyle.textDecorationColor = colorCssVar(decorationColor);
    }
    if (fontStyle && fontStyle !== 'italic' && fontStyle !== 'normal') {
      inlineStyle.fontStyle = fontStyle;
    }
    if (color) {
      inlineStyle.color = colorCssVar(color);
    }
    const Component = as as React.ElementType;

    return (
      <Component
        ref={ref}
        className={cn(
          VARIANT_CLASSES[variant] || VARIANT_CLASSES['body'],
          align && ALIGN[align],
          whiteSpace && WHITESPACE[whiteSpace],
          textTransform && TEXT_TRANSFORM[textTransform],
          decoration && DECORATION[decoration],
          fontStyle === 'italic' && 'italic',
          fontStyle === 'normal' && 'not-italic',
          weight && `font-${weight}`,
          size && SIZE_TO_TW[size],
          font && `font-${font}`,

          letterSpacing && `tracking-${letterSpacing}`,
          lineHeight && `leading-${lineHeight}`,
          className,
        )}
        style={
          Object.keys(inlineStyle).length > 0
            ? { ...inlineStyle, ...style }
            : style
        }
        {...rest}
      >
        {children}
      </Component>
    );
  },
);

Text.displayName = 'Text';

export default Text;
